<route lang="yaml">
    meta:
      layout: escuela-taller-home
      header: false
    </route>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Content, fetchOneEntry, isPreviewing } from '@builder.io/sdk-vue';
import { useRoute } from 'vue-router';
import { registeredComponents } from '@/init-builder.js';
import { useAuthStore } from "@/stores/auth";
import { BUILDER_API_KEY } from '@/config/builder';
import strapiService from '@/services/strapi';
import moment from 'moment';
// Importar moment-es
import 'moment/dist/locale/es';
import Talleres from '@/components/builder/Talleres.vue';
import axios from 'axios';
import NavbarTalleres from '@/components/escuela/NavbarTalleres.vue';
import HeaderTalleres from '@/components/escuela/HeaderTalleres.vue';
moment.locale('es');


// State
const content = ref(null);
const canShowContent = ref(false);
const isLoading = ref(true);
const isContentReady = ref(false);
const talleres = ref([]);
const isLoadingTalleres = ref(true);
const totalAlumnosPorTaller = ref({});
const totalAnecdotasPorTaller = ref({});
const estadisticasPorTaller = ref({});
const isLoadingStats = ref({});

// Mapeo de slugs a URLs de imágenes de Shopify
const shopifyImages = {
  'taller-1': 'https://cdn.shopify.com/s/files/1/0884/1638/7346/files/1_1virtual-shopify_hernan_casciari.webp?v=1746810761',
  'taller-2': 'https://cdn.shopify.com/s/files/1/0884/1638/7346/files/1_1virtual-shopify_hernan_casciari.webp?v=1746810761',
  'taller-marzo': 'https://cdn.shopify.com/s/files/1/0884/1638/7346/files/1_1virtual-shopify_hernan_casciari.webp?v=1746810761'
};

// Store and Route
const route = useRoute();
const store = useAuthStore();
const usuario = computed(() => store.usuario);
const esUsuario = computed(() => !!usuario.value);

// Variable de configuración para controlar la visibilidad del certificado
const mostrarCertificado = ref(false);

const getRegisteredComponents = () => {
  return registeredComponents;
};

// Cargar talleres del usuario
const cargarTalleres = async () => {
  try {
    isLoadingTalleres.value = true;
    const idUsuario = usuario.value?.id;

    console.log("idUsuario", idUsuario);

    if (!idUsuario) return;

    const response = await strapiService.getTalleresByUsuario(idUsuario, usuario.value?.email);

    // Inicializar el estado de loading para cada taller
    response?.forEach(taller => {
      isLoadingStats.value[taller.id] = true;
    });

    // Cargar los talleres inmediatamente
    talleres.value = response?.map(taller => {
      console.log("Taller de usuario", taller);
      let image, ruta;
      ruta = `/virtuales/${taller.docente?.toLowerCase().replace(' ', '-')}/${taller.slug}/inicio`;

      // Usar la imagen de Shopify si existe, sino usar la imagen del backend o una imagen por defecto
      image = taller.imagenPortada?.formats?.large?.url || taller.imagenPortada?.url || taller.agenda?.[0]?.thumbnail || "https://placehold.co/600x400";

      return {
        ...taller,
        id: taller.id,
        titulo: taller.titulo,
        docente: taller.docente,
        imagen: image,
        ruta,
        progreso: calcularProgresoTaller(taller),
        etapa: taller.etapa
      }
    }).sort((a, b) => a.id - b.id);

    response?.forEach(async taller => {
      try {
        const statsResponse = await strapiService.get(`/api/statistics/general/${taller.slug}`);
        const stats = statsResponse.data;

        console.log("stats", statsResponse);

        // Verificar que stats existe y tiene la estructura esperada
        if (!stats || !stats.general) {
          console.warn(`No se encontraron estadísticas para el taller ${taller.slug}`);
          throw new Error('Estructura de datos inválida');
        }

        // Mapear las estadísticas según el formato de la respuesta
        estadisticasPorTaller.value[taller.id] = {
          alumnos: stats.general.alumnos || 0,
          cuentos: stats.general.cuentos || 0,
          cuentos_con_audio: stats.general.cuentos_con_audio || 0,
          cuentos_con_resumenes: stats.general.cuentos_con_resumenes || 0,
          cuentos_con_votos_resumenes: stats.general.cuentos_con_votos_resumenes || 0,
          cuentos_con_votos_cuentos: stats.general.cuentos_con_votos_cuentos || 0,
          cuentos_con_votos_audios: stats.general.cuentos_con_votos_audios || 0,
          alumnos_votaron_resumenes: stats.general.alumnos_votaron_resumenes || 0,
          alumnos_votaron_cuentos: stats.general.alumnos_votaron_cuentos || 0,
          alumnos_votaron_audios: stats.general.alumnos_votaron_audios || 0,
          votos_resumenes: stats.general.votos_resumenes || 0,
          votos_lecturas: stats.general.votos_lecturas || 0,
          votos_audios: stats.general.votos_audios || 0,
          perfiles_completos: stats.general.perfiles_completos || 0,
          meta: stats.meta || []
        };

        totalAlumnosPorTaller.value[taller.id] = stats.general.alumnos || 0;
        totalAnecdotasPorTaller.value[taller.id] = stats.general.cuentos || 0;

        // Actualizar el progreso del taller
        const tallerIndex = talleres.value.findIndex(t => t.id === taller.id);
        if (tallerIndex !== -1) {
          const fechaActual = new Date();
          if (talleres.value[tallerIndex].agenda?.length > 0) {
            const fechaInicio = new Date(talleres.value[tallerIndex].agenda[0].fecha);
            const fechaFin = new Date(talleres.value[tallerIndex].agenda[talleres.value[tallerIndex].agenda.length - 1].fecha);
            const diferenciaDias = moment(fechaFin).diff(moment(fechaInicio), 'days');
            const diasTranscurridos = moment(fechaActual).diff(moment(fechaInicio), 'days');
            let progreso = (diasTranscurridos / diferenciaDias) * 100;
            progreso = Math.min(Math.round(progreso), 100);
            talleres.value[tallerIndex].progreso = progreso;
          }
        }

        console.log(`Estadísticas cargadas para taller ${taller.slug}:`, {
          general: stats.general,
          meta: stats.meta
        });

      } catch (error) {
        console.error(`Error al cargar estadísticas para el taller ${taller.slug}:`, error);
        estadisticasPorTaller.value[taller.id] = {
          alumnos: 0,
          cuentos: 0,
          cuentos_con_audio: 0,
          cuentos_con_resumenes: 0,
          cuentos_con_votos_resumenes: 0,
          cuentos_con_votos_cuentos: 0,
          cuentos_con_votos_audios: 0,
          alumnos_votaron_resumenes: 0,
          alumnos_votaron_cuentos: 0,
          alumnos_votaron_audios: 0,
          votos_resumenes: 0,
          votos_lecturas: 0,
          votos_audios: 0,
          perfiles_completos: 0,
          meta: []
        };
        totalAlumnosPorTaller.value[taller.id] = 0;
        totalAnecdotasPorTaller.value[taller.id] = 0;
      } finally {
        isLoadingStats.value[taller.id] = false;
      }
    });

  } finally {
    isLoadingTalleres.value = false;
  }
};

// Cargar contenido de Builder
const loadContent = async () => {
  try {
    console.log("BUILDER_API_KEY", BUILDER_API_KEY);
    const contentValue = await fetchOneEntry({
      model: 'page',
      apiKey: BUILDER_API_KEY,
      userAttributes: {
        urlPath: `/escuela-shopify`,
      },
      cacheSeconds: -1,
    });

    document.title = contentValue?.data?.title || 'Escuela';
    content.value = contentValue;
    canShowContent.value = contentValue ? true : isPreviewing();
    isContentReady.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadContent();
  cargarTalleres();
});

const getPercentageOfTotal = (value, total) => {
  if (!value || !total) return 0;
  const numValue = Number(value);
  const numTotal = Number(total);
  const percentage = Math.round((numValue / numTotal) * 100);
  return Math.min(percentage, 100);
};

const formatNumber = (value) => {
  if (value === undefined || value === null) return '0';
  return Number(value).toLocaleString('es-AR');
};

// Función para calcular días restantes hasta la primera clase
const calcularDiasRestantes = (taller) => {
  if (!taller?.agenda?.length) return null;

  const primeraClase = new Date(taller.agenda[0].fecha);
  const hoy = new Date();

  // Si la fecha ya pasó, retornar null
  if (primeraClase < hoy) return null;

  const diasRestantes = moment(primeraClase).diff(moment(hoy), 'days');
  return diasRestantes;
};

// Función para formatear la fecha del taller
const formatearFechaTaller = (taller) => {
  if (!taller?.agenda?.length) return '';
  const primeraClase = new Date(taller.agenda[0].fecha);
  const mes = primeraClase.toLocaleDateString('es-ES', { month: 'long' }).toUpperCase();
  const anio = primeraClase.getFullYear();
  return `${mes} | ${anio}`;
};

const calcularProgresoTaller = (taller) => {
  if (!taller?.agenda?.length) return 0;
  const clasesOrdenadas = [...taller.agenda].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  const primeraClase = new Date(clasesOrdenadas[0].fecha);
  const ultimaClase = new Date(clasesOrdenadas[clasesOrdenadas.length - 1].fecha);
  const ahora = new Date();
  if (ahora < primeraClase) return 0;
  if (ahora > ultimaClase) return 100;
  const total = ultimaClase - primeraClase;
  const transcurrido = ahora - primeraClase;
  if (total === 0) return 100;
  return Math.min(100, Math.max(0, Math.round((transcurrido / total) * 100)));
};
</script>

<template>
  <div>
    <HeaderTalleres />
    <div class="max-w-6xl mx-auto mb-10 lg:mb-16 md:p-5 pt-0 px-2 relative">
      <!-- Sección de Mis talleres solo visible para usuarios logueados -->
      <template v-if="esUsuario">

        <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6 px-2 md:px-0 mt-6">Zona personal:<br
            class="md:hidden" /> Acá están tus talleres</h2>

        <!-- Loading state -->
        <div v-if="isLoadingTalleres" class="flex justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>

        <!-- Mensaje si no hay talleres -->
        <div v-else-if="!talleres || talleres.length === 0" class="text-center py-12 px-4">
          <div class="max-w-sm mx-auto">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h3 class="mt-2 text-lg font-medium text-gray-900">Aún no estás inscripto a ningún taller</h3>
            <p class="mt-1 text-sm text-gray-500">Explorá los talleres disponibles y encontrá el que mejor se adapte a
              vos.</p>
          </div>
        </div>

        <!-- Talleres grid -->
        <div v-else class="flex flex-col gap-6">
          <div v-for="taller in talleres" :key="taller.id"
            class="bg-white rounded-2xl shadow-md border border-gray-200 flex flex-row items-stretch overflow-hidden flex-wrap">
            <!-- Imagen del taller -->
            <div class="w-full md:w-[22%] flex-shrink-0">
              <router-link :to="taller.ruta" class="block w-full h-full">
                <div class="relative w-full aspect-square">
                  <img :src="taller.imagen" :alt="taller.titulo" class="absolute inset-0 w-full h-full object-cover" />
                </div>
              </router-link>
            </div>
            <!-- Contenido del taller -->
            <div class="flex flex-col md:flex-row p-2 md:p-4 gap-4 flex-1">
              <!-- Info principal del taller -->
              <div class="min-w-0 flex flex-col justify-center w-full md:w-[200px]">
                <div class="text-xs text-gray-600 font-semibold uppercase mb-1 tracking-wide">
                  {{ formatearFechaTaller(taller) }}</div>
                <h3 class="text-2xl font-extrabold text-black mb-4 leading-tight break-words w-full">
                  {{ taller.titulo }}
                </h3>
                <div class="mt-auto">
                  <span class="text-base font-bold text-black">Docente</span><br>
                  <span class="text-base text-gray-800">{{ taller.docente }}</span>
                </div>
              </div>
              <!-- Métricas y progreso -->
              <div class="flex flex-col md:flex-row flex-wrap gap-4 flex-1 w-full">
                <!-- Card Alumnos SOLO MOBILE -->
                <div class="flex md:hidden bg-white border border-gray-200 rounded-2xl shadow flex-col justify-between p-4 w-full min-w-[180px] mb-2">
                  <div class="flex items-center justify-between w-full">
                    <div class="flex items-center gap-2">
                      <span class="bg-blue-100 text-blue-600 rounded-xl p-2 flex items-center justify-center">
                        <img src="@/assets/images/Talleres/alumnos.png" alt="Alumnos" class="w-6 h-6 object-contain" />
                      </span>
                      <span class="text-md font-bold text-gray-800 leading-tight">Alumnos</span>
                    </div>
                    <div v-if="isLoadingStats[taller.id]" class="flex justify-center">
                      <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                    </div>
                    <div v-else class="text-lg font-extrabold text-gray-900">
                      {{ formatNumber(totalAlumnosPorTaller[taller.id] || 0) }}
                    </div>
                  </div>
                </div>
                <!-- Card Alumnos - Oculto en móvil -->
                <div class="hidden md:flex bg-white border border-gray-200 rounded-2xl shadow flex-col justify-between p-4 w-full md:flex-1 min-w-[180px]">
                  <div class="flex items-center gap-2 mb-2">
                    <span class="bg-blue-100 text-blue-600 rounded-xl p-2 flex items-center justify-center">
                      <img src="@/assets/images/Talleres/alumnos.png" alt="Alumnos" class="w-6 h-6 object-contain" />
                    </span>
                    <div>
                      <div class="text-sm font-bold text-gray-800 leading-tight">Alumnos</div>
                      <div class="text-xs text-gray-500">Participación y actividad</div>
                    </div>
                  </div>
                  <div v-if="isLoadingStats[taller.id]" class="flex justify-center py-4">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                  </div>
                  <template v-else>
                    <div class="text-2xl font-extrabold text-gray-900 mb-1">{{
                      formatNumber(totalAlumnosPorTaller[taller.id] || 0) }}
                    </div>
                    <div class="text-sm text-gray-500 mb-2">Total de alumnos</div>
                    <div class="mt-auto">
                      <div class="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Perfiles completos</span>
                        <span class="font-bold text-black">{{
                          formatNumber(estadisticasPorTaller[taller.id]?.perfiles_completos || 0) }}
                          <span class="text-gray-400">({{
                            getPercentageOfTotal(estadisticasPorTaller[taller.id]?.perfiles_completos,
                              totalAlumnosPorTaller[taller.id]) }}%)</span></span>
                      </div>
                      <div class="w-full h-2 bg-gray-200 rounded-full">
                        <div class="h-2 bg-green-500 rounded-full"
                          :style="{ width: `${getPercentageOfTotal(estadisticasPorTaller[taller.id]?.perfiles_completos, totalAlumnosPorTaller[taller.id])}%` }">
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
                <!-- Card Anécdotas - Oculto en móvil -->
                <div class="hidden md:flex bg-white border border-gray-200 rounded-2xl shadow flex-col justify-between p-4 w-full md:flex-1 min-w-[180px]">
                  <div class="flex items-center gap-2 mb-2">
                    <span class="bg-purple-100 text-purple-600 rounded-xl p-2 flex items-center justify-center">
                      <img src="@/assets/images/Talleres/anecdotas.png" alt="Tareas" class="w-6 h-6 object-contain" />
                    </span>
                    <div>
                      <div class="text-sm font-bold text-gray-800 leading-tight">{{ taller.terminoPlural ?
                        taller.terminoPlural.charAt(0).toUpperCase() + taller.terminoPlural.slice(1) : 'Tareas' }}
                      </div>
                      <div class="text-xs text-gray-500">Contexto y valoraciones</div>
                    </div>
                  </div>
                  <div v-if="isLoadingStats[taller.id]" class="flex justify-center py-4">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                  </div>
                  <template v-else>
                    <div class="text-2xl font-extrabold text-gray-900 mb-1">{{
                      formatNumber(totalAnecdotasPorTaller[taller.id] || 0) }}
                    </div>
                    <div class="mt-auto">
                      <div class="flex flex-col gap-2">
                        <template v-if="taller.docente?.toLowerCase().includes('hernan casciari')">
                          <div>
                            <div class="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Con audio</span>
                              <span class="font-bold text-black">{{
                                formatNumber(estadisticasPorTaller[taller.id]?.cuentos_con_audio || 0) }}
                                <span class="text-gray-400">({{
                                  getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_audio,
                                    totalAnecdotasPorTaller[taller.id]) }}%)</span></span>
                            </div>
                            <div class="w-full h-2 bg-gray-200 rounded-full">
                              <div class="h-2 bg-blue-500 rounded-full"
                                :style="{ width: `${getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_audio, totalAnecdotasPorTaller[taller.id])}%` }">
                              </div>
                            </div>
                          </div>
                          <div>
                            <div class="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Con resúmenes</span>
                              <span class="font-bold text-black">{{
                                formatNumber(estadisticasPorTaller[taller.id]?.cuentos_con_resumenes || 0) }}
                                <span class="text-gray-400">({{
                                  getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_resumenes,
                                    totalAnecdotasPorTaller[taller.id]) }}%)</span></span>
                            </div>
                            <div class="w-full h-2 bg-gray-200 rounded-full">
                              <div class="h-2 bg-purple-500 rounded-full"
                                :style="{ width: `${getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_resumenes, totalAnecdotasPorTaller[taller.id])}%` }">
                              </div>
                            </div>
                          </div>
                        </template>
                        <template v-else>
                          <div>
                            <div class="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Total de {{ taller.terminoPlural?.toLowerCase() || 'tareas' }}</span>
                              <span class="font-bold text-black">{{
                                formatNumber(totalAnecdotasPorTaller[taller.id] || 0) }}
                              </span>
                            </div>
                            <div class="w-full h-2 bg-gray-200 rounded-full">
                              <div class="h-2 bg-purple-500 rounded-full"
                                :style="{ width: totalAnecdotasPorTaller[taller.id] > 0 ? '100%' : '0%' }">
                              </div>
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </template>
                </div>
                <!-- Card de progreso (visible en móvil y desktop) -->
                <div class="flex flex-col justify-between w-full md:flex-1 items-end">
                  <div class="w-full">
                    <div class="text-sm font-bold text-black mb-1">Progreso</div>
                    <div class="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
                      <div v-if="taller.progreso >= 0" :class="taller.progreso >= 100 ? 'bg-black' : 'bg-[#22C55E]'"
                        :style="{ width: Math.min(taller.progreso, 100) + '%' }"
                        class="h-full rounded-full transition-all"></div>
                    </div>
                    <div v-if="taller.progreso >= 0" class="text-right mb-4">
                      <template v-if="taller.progreso >= 100">
                        <span class="text-sm font-bold text-black">Taller finalizado</span>
                      </template>
                      <template v-else>
                        <span class="text-lg font-bold text-black">{{ Math.min(taller.progreso, 100) }}%</span>
                      </template>
                    </div>
                    <div v-else class="text-right mb-4">
                      <template v-if="calcularDiasRestantes(taller) !== null">
                        <span class="text-sm font-bold text-black">Comienza en {{ calcularDiasRestantes(taller) }}
                          días</span>
                      </template>
                      <template v-else>
                        <span class="text-sm font-bold text-gray-600">Próximamente</span>
                      </template>
                    </div>
                  </div>
                  <router-link v-if="taller.progreso >= 100 && mostrarCertificado" :to="taller.ruta"
                    class="w-full px-4 py-2 border border-black rounded-lg text-base font-semibold bg-white text-black hover:bg-gray-100 transition block text-center">Descargar
                    certificado</router-link>
                  <router-link v-else :to="taller.ruta"
                    class="w-full px-4 py-2 bg-black text-white rounded-lg text-base font-semibold hover:bg-gray-800 flex items-center justify-center transition block text-center">Ir
                    al taller <svg class="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg></router-link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr class="my-12 border-gray-200" />
      </template>

      <!-- Bloque de todos los talleres para comprar -->
      <div class="mt-16" :class="{ 'mt-0': !esUsuario }">
        <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-6 px-4 md:px-0">
          {{ esUsuario ? 'Otros talleres que te pueden interesar' : 'Todos los talleres' }}</h2>
        <Talleres />
      </div>
      <hr class="my-12 border-gray-200" />

    </div>
  </div>
</template>

<style lang="scss">
.fade {

  &-enter-active,
  &-leave-active {
    transition: all 0.5s ease;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
  }

  &-enter-to,
  &-leave-from {
    opacity: 1;
  }
}

/* Ajustes responsive */
@media (max-width: 768px) {
  .grid {
    gap: 1rem;
  }

  .p-4 {
    padding: 0.75rem;
  }
}
</style>

<style>
.aspect-square {
  aspect-ratio: 1 / 1;
}
</style>
